﻿/**
 * GA4 Menu Tracking Controller
 * Tracks menu impressions and clicks for elements with data-ga-track="menu"
 */

(function () {
  'use strict';

  const GLOBAL_FLAG = '__gaMenuTrackerInitialized';
  if (window[GLOBAL_FLAG]) return;
  window[GLOBAL_FLAG] = true;

  const MENU_SELECTOR = 'a[data-ga-track="menu"]';
  const CART_ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const IMPRESSION_SENT_ATTR = 'data-ga-impression-sent';
  const COLLECTION_ATTRIBUTION_STORAGE_KEY = 'fancime_last_collection_menu_attribution';
  const ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;
  const ATTRIBUTION_MAX_AGE_MS = ATTRIBUTION_MAX_AGE_SECONDS * 1000;
  const COLLECTION_VIEW_GUARD_PREFIX = 'ga_menu_view_category_';
  const ADD_TO_CART_GUARD_MS = 1500;
  const DESKTOP_BREAKPOINT = 750;
  const G_TAG_RETRY_INTERVAL = 250;
  const G_TAG_MAX_WAIT = 10000;
  let lastAddToCartTrackedAt = 0;

  const getDeviceType = () =>
    window.matchMedia(`(max-width: ${DESKTOP_BREAKPOINT - 1}px)`).matches ? 'm' : 'pc';

  const getDeviceSuffix = () => (getDeviceType() === 'pc' ? '-pc' : '-m');

  const getAttr = (el, key) => (el && el.getAttribute(key) ? el.getAttribute(key).trim() : '');
  const hasTrackableUrl = (url) => !!url && url !== '#' && !/^javascript:/i.test(url);
  const isCollectionUrl = (url) => {
    if (!hasTrackableUrl(url)) return false;

    try {
      return /\/collections\/[^/]+/.test(new URL(url, window.location.origin).pathname);
    } catch (error) {
      return false;
    }
  };
  const isElementTrackableForDevice = (el) => {
    const scope = getAttr(el, 'data-ga-device-scope');
    if (!scope) return true;
    return (scope === 'desktop' && getDeviceType() === 'pc') || (scope === 'mobile' && getDeviceType() === 'm');
  };

  const getMenuUrl = (el) => {
    const dataUrl = getAttr(el, 'data-ga-url');
    if (hasTrackableUrl(dataUrl)) return dataUrl;

    if (el instanceof HTMLAnchorElement && el.href) return el.href;
    const link = el.querySelector && el.querySelector('a[href]');
    return link ? link.href : '';
  };

  const getMenuHandleFromUrl = (url) => {
    if (!hasTrackableUrl(url)) return '';

    try {
      return new URL(url, window.location.origin).pathname.split('/').filter(Boolean).pop() || '';
    } catch (error) {
      return '';
    }
  };

  const normalizeId = (value) =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const getMenuId = (el, menuUrl) => {
    const explicitId = normalizeId(getAttr(el, 'data-ga-id'));
    if (explicitId) return explicitId;

    const urlHandle = normalizeId(getMenuHandleFromUrl(menuUrl));
    if (urlHandle) return urlHandle;

    return normalizeId(getAttr(el, 'data-ga-name'));
  };

  const getMenuLevel = (el) => {
    const level = getAttr(el, 'data-ga-level');
    return `level_${level || 'unknown'}`;
  };

  const buildPayload = (el, isImpression) => {
    const menuName = getAttr(el, 'data-ga-name');
    const menuUrl = getMenuUrl(el);
    if (!menuName || !hasTrackableUrl(menuUrl) || !isElementTrackableForDevice(el)) return null;

    const menuId = getMenuId(el, menuUrl);
    const payload = {
      menu_id: menuId,
      menu_name: `${menuName}${getDeviceSuffix()}`,
      menu_level: getMenuLevel(el),
      menu_url: menuUrl,
      device_type: getDeviceType()
    };

    if (window.Shopify && window.Shopify.locale) payload.locale = window.Shopify.locale;
    if (window.Shopify && window.Shopify.country) payload.country = window.Shopify.country;

    const label = getAttr(el, 'data-ga-label');
    if (label) payload.menu_label = label;

    if (isImpression) payload.non_interaction = true;
    if (!isImpression) payload.transport_type = 'beacon';

    if (typeof window.GA_MENU_TRACKER_SEND_TO === 'string' && window.GA_MENU_TRACKER_SEND_TO.trim()) {
      payload.send_to = window.GA_MENU_TRACKER_SEND_TO.trim();
    }

    return payload;
  };

  const sendEvent = (eventName, payload) => {
    if (!payload || typeof window.gtag !== 'function') return false;
    window.gtag('event', eventName, payload);
    return true;
  };

  const readMenuAttribution = () => {
    try {
      const raw = window.localStorage.getItem(COLLECTION_ATTRIBUTION_STORAGE_KEY);
      if (!raw) return null;

      const attribution = JSON.parse(raw);
      if (!attribution || !attribution.menu_name) return null;

      if (attribution.captured_at) {
        const capturedAt = new Date(attribution.captured_at).getTime();
        if (Number.isFinite(capturedAt) && Date.now() - capturedAt > ATTRIBUTION_MAX_AGE_MS) return null;
      }

      return attribution;
    } catch (error) {
      return null;
    }
  };

  const buildAttributedPayload = (extraParams = {}) => {
    const attribution = readMenuAttribution();
    if (!attribution) return null;

    const payload = {
      menu_id: attribution.menu_id || '',
      collection_menu_name: attribution.menu_name,
      menu_name: attribution.menu_name,
      menu_level: attribution.menu_level || 'level_unknown',
      menu_url: attribution.menu_url || '',
      device_type: attribution.device_type || getDeviceType(),
      locale: attribution.locale || (window.Shopify && window.Shopify.locale) || '',
      country: attribution.country || (window.Shopify && window.Shopify.country) || '',
      current_url: window.location.pathname
    };

    if (typeof window.GA_MENU_TRACKER_SEND_TO === 'string' && window.GA_MENU_TRACKER_SEND_TO.trim()) {
      payload.send_to = window.GA_MENU_TRACKER_SEND_TO.trim();
    }

    return { ...payload, ...extraParams };
  };

  const saveMenuAttribution = (payload) => {
    if (!payload) return;

    const attribution = {
      menu_id: payload.menu_id,
      menu_name: payload.menu_name,
      menu_level: payload.menu_level,
      menu_url: payload.menu_url,
      device_type: payload.device_type,
      locale: payload.locale,
      country: payload.country,
      captured_at: new Date().toISOString()
    };
    const value = JSON.stringify(attribution);

    try {
      window.localStorage.setItem(COLLECTION_ATTRIBUTION_STORAGE_KEY, value);
    } catch (error) {
      // Storage can be unavailable in private or restricted browser modes.
    }

    try {
      document.cookie = `${COLLECTION_ATTRIBUTION_STORAGE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${ATTRIBUTION_MAX_AGE_SECONDS}; SameSite=Lax`;
    } catch (error) {
      // Cookie write failures should not block normal navigation.
    }
  };

  const clearCollectionAttribution = () => {
    try {
      window.localStorage.removeItem(COLLECTION_ATTRIBUTION_STORAGE_KEY);
    } catch (error) {
      // Storage can be unavailable in private or restricted browser modes.
    }

    try {
      document.cookie = `${COLLECTION_ATTRIBUTION_STORAGE_KEY}=; path=/; max-age=0; SameSite=Lax`;
    } catch (error) {
      // Cookie write failures should not block normal navigation.
    }
  };

  const observeImpressions = () => {
    if (!window.IntersectionObserver) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.getAttribute(IMPRESSION_SENT_ATTR) === '1') {
            observer.unobserve(el);
            return;
          }

          const payload = buildPayload(el, true);
          const sent = sendEvent('menu_impression', payload);

          if (sent) {
            el.setAttribute(IMPRESSION_SENT_ATTR, '1');
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.2 }
    );

    const observeCurrentMenus = () => {
      document.querySelectorAll(MENU_SELECTOR).forEach((el) => {
        if (el.getAttribute(IMPRESSION_SENT_ATTR) !== '1') observer.observe(el);
      });
    };

    observeCurrentMenus();

    if (window.MutationObserver) {
      const mo = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            if (node.matches && node.matches(MENU_SELECTOR)) {
              observer.observe(node);
            }

            if (node.querySelectorAll) {
              node.querySelectorAll(MENU_SELECTOR).forEach((el) => observer.observe(el));
            }
          });
        });
      });

      mo.observe(document.body, { childList: true, subtree: true });
    }
  };

  const bindClickTracking = () => {
    document.addEventListener(
      'click',
      (event) => {
        const source = event.target;
        if (!(source instanceof Element)) return;

        const target = source.closest(MENU_SELECTOR);
        if (!target) return;

        const payload = buildPayload(target, false);
        if (payload && isCollectionUrl(payload.menu_url)) {
          saveMenuAttribution(payload);
        } else {
          clearCollectionAttribution();
        }
        sendEvent('menu_click', payload);
      },
      { capture: true }
    );
  };

  const trackCollectionView = () => {
    if (!isCollectionUrl(window.location.href)) return;

    const guardKey = `${COLLECTION_VIEW_GUARD_PREFIX}${window.location.pathname}`;
    try {
      if (window.sessionStorage.getItem(guardKey) === '1') return;
      window.sessionStorage.setItem(guardKey, '1');
    } catch (error) {
      // Session storage is a best-effort guard only.
    }

    sendEvent(
      'menu_view_category',
      buildAttributedPayload({
        menu_view_category_count: 1,
        non_interaction: true
      })
    );
  };

  const trackAddToCart = () => {
    const now = Date.now();
    if (now - lastAddToCartTrackedAt < ADD_TO_CART_GUARD_MS) return;
    lastAddToCartTrackedAt = now;

    sendEvent(
      'menu_add_to_cart',
      buildAttributedPayload({
        menu_add_to_cart_count: 1,
        transport_type: 'beacon'
      })
    );
  };

  const bindAddToCartTracking = () => {
    document.addEventListener('theme:product:add', trackAddToCart);

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches(CART_ADD_FORM_SELECTOR)) return;

        window.setTimeout(trackAddToCart, 300);
      },
      { capture: true }
    );
  };

  const startTracking = () => {
    bindClickTracking();
    bindAddToCartTracking();
    trackCollectionView();
    observeImpressions();
  };

  const waitForGtag = () => {
    const startedAt = Date.now();

    const tick = () => {
      if (typeof window.gtag === 'function') {
        startTracking();
        return;
      }

      if (Date.now() - startedAt >= G_TAG_MAX_WAIT) {
        startTracking();
        return;
      }

      window.setTimeout(tick, G_TAG_RETRY_INTERVAL);
    };

    tick();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGtag, { once: true });
  } else {
    waitForGtag();
  }
})();
